import { signRequest, type HttpRequest } from '@aws-amplify/core/internals/aws-client-utils';
import { Amplify } from 'aws-amplify';
import { confirmSignIn, fetchAuthSession, getCurrentUser, signIn, signOut } from 'aws-amplify/auth';

import type { AnalyticsRequest } from '@/lib/apiClient';

export type AnalyticsOperator = {
  email: string | null;
  groups: string[];
  username: string;
};

export type AnalyticsSession =
  | { status: 'authenticated'; operator: AnalyticsOperator }
  | { status: 'unauthenticated' }
  | { status: 'unauthorized'; operator: AnalyticsOperator };

export type AnalyticsSignInResult = { status: 'signedIn' } | { status: 'newPasswordRequired' };

const operatorGroupName = 'InternalAnalyticsOperators';
const defaultRegion = 'eu-north-1';
type AnalyticsEnvName =
  'VITE_ANALYTICS_AWS_REGION' | 'VITE_ANALYTICS_IDENTITY_POOL_ID' | 'VITE_ANALYTICS_USER_POOL_CLIENT_ID' | 'VITE_ANALYTICS_USER_POOL_ID';

let configured = false;

const requiredEnv = (name: AnalyticsEnvName): string => {
  const value = import.meta.env[name];
  if (!value) {
    throw new Error(`Missing analytics frontend env ${name}`);
  }
  return value;
};

const optionalEnv = (name: AnalyticsEnvName): string | undefined => {
  const value = import.meta.env[name];
  return value && value.length > 0 ? value : undefined;
};

const analyticsRegion = (): string =>
  optionalEnv('VITE_ANALYTICS_AWS_REGION') ?? requiredEnv('VITE_ANALYTICS_IDENTITY_POOL_ID').split(':')[0] ?? defaultRegion;

const configureAuth = (): void => {
  if (configured) return;

  Amplify.configure({
    Auth: {
      Cognito: {
        allowGuestAccess: false,
        identityPoolId: requiredEnv('VITE_ANALYTICS_IDENTITY_POOL_ID'),
        userPoolClientId: requiredEnv('VITE_ANALYTICS_USER_POOL_CLIENT_ID'),
        userPoolId: requiredEnv('VITE_ANALYTICS_USER_POOL_ID'),
      },
    },
  });

  configured = true;
};

const decodeBase64Url = (value: string): string => {
  const padded = value.padEnd(value.length + ((4 - (value.length % 4)) % 4), '=');
  const base64 = padded.replaceAll('-', '+').replaceAll('_', '/');
  return globalThis.atob(base64);
};

const decodeJwtPayload = (token: string): Record<string, unknown> => {
  const [, payload] = token.split('.');
  if (!payload) return {};
  return JSON.parse(decodeBase64Url(payload)) as Record<string, unknown>;
};

const stringClaim = (claims: Record<string, unknown>, name: string): string | null => {
  const value = claims[name];
  return typeof value === 'string' ? value : null;
};

const groupClaims = (claims: Record<string, unknown>): string[] => {
  const value = claims['cognito:groups'];
  return Array.isArray(value) ? value.filter((group): group is string => typeof group === 'string') : [];
};

const operatorFromClaims = (username: string, claims: Record<string, unknown>): AnalyticsOperator => ({
  email: stringClaim(claims, 'email'),
  groups: groupClaims(claims),
  username,
});

export const loadAnalyticsSession = async (): Promise<AnalyticsSession> => {
  configureAuth();

  const session = await fetchAuthSession({ forceRefresh: false });
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) {
    return { status: 'unauthenticated' };
  }

  const user = await getCurrentUser();
  const claims = decodeJwtPayload(idToken);
  const operator = operatorFromClaims(user.username, claims);

  if (!operator.groups.includes(operatorGroupName)) {
    return { operator, status: 'unauthorized' };
  }

  return { operator, status: 'authenticated' };
};

const signInResultFromStep = (isSignedIn: boolean, signInStep?: string): AnalyticsSignInResult => {
  if (isSignedIn) {
    return { status: 'signedIn' };
  }

  if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
    return { status: 'newPasswordRequired' };
  }

  throw new Error(`Unsupported analytics sign-in step: ${signInStep ?? 'unknown'}`);
};

export const signInAnalyticsOperator = async (email: string, password: string): Promise<AnalyticsSignInResult> => {
  configureAuth();

  const result = await signIn({ username: email, password });
  return signInResultFromStep(result.isSignedIn, result.nextStep.signInStep);
};

export const completeAnalyticsNewPassword = async (password: string): Promise<AnalyticsSignInResult> => {
  configureAuth();

  const result = await confirmSignIn({ challengeResponse: password });
  return signInResultFromStep(result.isSignedIn, result.nextStep.signInStep);
};

export const signOutAnalyticsOperator = async (): Promise<void> => {
  configureAuth();
  await signOut();
};

export const signAnalyticsRequest = async (request: AnalyticsRequest): Promise<AnalyticsRequest> => {
  configureAuth();

  const session = await fetchAuthSession({ forceRefresh: false });
  if (!session.credentials) {
    throw new Error('Analytics operator credentials are not available');
  }

  const url = new URL(request.url, globalThis.location.origin);
  const signed = signRequest(
    {
      body: request.body,
      headers: request.headers,
      method: request.method,
      url,
    } satisfies HttpRequest,
    {
      credentials: session.credentials,
      signingRegion: analyticsRegion(),
      signingService: 'execute-api',
    },
  );

  return {
    ...request,
    credentials: 'omit',
    headers: signed.headers,
  };
};
